"use client";

import { ArrowRight, ChevronDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Restoration() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const services = ["ROOF CLEANING", "GUTTER REPAIR", "PRESSURE WASHING", "MOSS REMOVAL", "GUTTER CLEARING", "ROOF MAINTENANCE", "COMMERCIAL EXTERIORS", "DOMESTIC RESTORATION", "SURFACE SEALING", "INDUSTRIAL CLEANING"];
  return <section data-section-id="4938" id="the-exterior-restoration-hub" className="relative min-h-screen w-full overflow-hidden bg-background text-foreground dark" style={{
    backgroundImage: "radial-gradient(circle at 20% 30%, hsla(48, 95%, 50%, 0.04) 0%, transparent 50%), radial-gradient(circle at 80% 70%, hsla(48, 95%, 50%, 0.03) 0%, transparent 50%)"
  }}>
      {/* Texture overlay */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.08] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
    }} />

      {/* Decorative vertical line */}
      <div className="pointer-events-none absolute top-0 bottom-0 hidden lg:block" style={{
      left: "60%",
      width: "1px",
      background: "linear-gradient(to bottom, transparent, hsla(48, 95%, 50%, 0.4), transparent)"
    }} />

      <div className="relative mx-auto flex min-h-screen max-w-[1600px] flex-col px-6 pt-10 pb-32 sm:px-10 lg:px-16">
        <div className="grid flex-1 grid-cols-1 gap-12 lg:grid-cols-[60%_40%] lg:gap-0">
          {/* Left column */}
          <div ref={leftRef} className={`flex flex-col justify-center pr-0 transition-all duration-1000 ease-out lg:pr-12 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {/* Eyebrow */}
            <div className="mb-10 flex items-center gap-4">
              <div className="h-px w-12 flex-shrink-0" style={{
              background: "var(--primary)"
            }} />
              <span className="font-sans text-xs font-medium uppercase tracking-[0.2em] !text-[rgba(215,98,22,1)]" style={{
              color: "var(--primary)"
            }}>MacKenzie Exterior Services · EXTERIOR RESTORATION</span>
            </div>

            {/* Display heading */}
            <h1 className="font-default font-black uppercase leading-[0.9] text-foreground" style={{
            fontSize: "clamp(3rem, 9vw, 11rem)",
            letterSpacing: "-0.04em",
            fontWeight: 900
          }}>
              <span className="block">ELITE</span>
              <span className="block !text-[rgba(215,98,22,0)]" style={{
              color: "transparent",
              WebkitTextStroke: "2px var(--primary)"
            }}><span className="!text-[rgba(215,98,22,1)]">ROOF</span></span>
              <span className="block">CARE</span>
            </h1>

            {/* Intro paragraph */}
            <p className="mt-10 max-w-[480px] font-sans text-base leading-relaxed text-foreground/70 sm:text-lg">Professional roof cleaning, gutter maintenance, and industrial pressure washing. We restore the structural integrity and aesthetic value of domestic and commercial properties.&nbsp;</p>

            {/* Supporting Trust & Performance Box */}
            <div className="mt-6 max-w-[480px] border border-foreground/15 p-4 sm:p-5" style={{
            background: "hsla(0, 0%, 100%, 0.04)",
            boxShadow: "inset 0 1px 0 0 hsla(0, 0%, 100%, 0.05)"
          }}>
              <div className="flex items-center gap-3">
                <span className="font-mono text-[11px] font-semibold uppercase tracking-wider" style={{
                color: "var(--primary)"
              }}>
                  VERIFIED RESTORATION RECORD
                </span>
                <span className="h-px flex-1 bg-foreground/10" />
              </div>
              <p className="mt-2 font-sans text-sm leading-relaxed text-foreground/85">Over 500+ successful exterior restorations completed with a 100% customer satisfaction rate across residential and commercial structures.</p>
            </div>

            {/* CTA buttons */}
            <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
              <Link to="#">
                <Button size="lg" className="group h-14 bg-primary px-8 font-sans text-sm font-bold uppercase tracking-[0.15em] text-primary-foreground hover:bg-primary/90" style={{
                boxShadow: "8px 8px 0px 0px hsl(0 0% 0%)"
              }}>
                  REQUEST A QUOTE
                  <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Right column */}
          <div ref={rightRef} className={`relative flex items-center justify-center transition-all duration-1000 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="absolute bottom-[8%] left-1/2 h-8 w-[70%] -translate-x-1/2 blur-2xl" style={{
            background: "hsla(0, 0%, 0%, 0.8)"
          }} />

            <div className="absolute top-1/2 left-1/2 h-[60%] w-[50%] -translate-x-1/2 -translate-y-1/2 blur-3xl" style={{
            background: "hsla(48, 95%, 50%, 0.12)"
          }} />

            <div className="relative h-full max-h-[700px] w-full max-w-[420px]">
              <img src="https://deciding-elf.10web.cloud/wp-content/uploads/2026/08/559549102_908578818995361_3431574949486946791_n.jpg" alt="Professional roof cleaning and restoration service" loading="eager" decoding="async" className="relative z-10 h-full w-full object-contain" style={{
              filter: "drop-shadow(0 40px 30px hsla(0, 0%, 0%, 0.7))"
            }} />

              <div className="absolute bottom-[6%] -left-4 z-20 w-[280px] border border-foreground/15 p-5 backdrop-blur-xl sm:-left-8" style={{
              background: "hsla(0, 0%, 100%, 0.06)",
              boxShadow: "0 25px 50px -12px hsla(0, 0%, 0%, 0.8), inset 0 1px 0 0 hsla(0, 0%, 100%, 0.1)"
            }}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="font-default text-[10px] font-bold uppercase tracking-[0.18em] text-foreground/60">MACKENZIE EXTERIOR SERVICES</div>
                    <div className="mt-2 font-default text-sm font-bold uppercase tracking-[0.1em] text-foreground">
                      EXTERIOR CARE
                    </div>
                    <div className="mt-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-foreground/50">
                      <span>CERTIFIED</span>
                      <span className="size-1 rounded-lg bg-foreground/30" />
                      <span>INSURED</span>
                    </div>
                  </div>
                  <div className="flex-shrink-0 px-2 py-1 font-default text-xs font-black" style={{
                  background: "var(--primary)",
                  color: "var(--primary-foreground)"
                }}>
                    PRO
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="pointer-events-none absolute bottom-32 left-6 hidden flex-col items-center gap-3 sm:left-10 lg:flex">
          <span className="font-default text-[10px] font-bold uppercase tracking-[0.3em]" style={{
          color: "var(--primary)",
          writingMode: "vertical-rl",
          transform: "rotate(180deg)"
        }}>
            SCROLL TO DISCOVER
          </span>
          <div className="relative h-16 w-px overflow-hidden bg-foreground/15">
            <div className="absolute top-0 left-0 h-4 w-full animate-[scrollLine_2s_ease-in-out_infinite]" style={{
            background: "var(--primary)"
          }} />
          </div>
          <ChevronDown className="size-3" style={{
          color: "var(--primary)"
        }} />
        </div>
      </div>

      {/* Marquee ticker */}
      <div className="absolute bottom-0 left-0 right-0 overflow-hidden border-t border-foreground/10 py-5" style={{
      background: "hsla(0, 0%, 0%, 0.4)"
    }}>
        <div className="flex animate-[marquee_40s_linear_infinite] whitespace-nowrap">
          {[...services, ...services, ...services].map((b, i) => <div key={i} data-index={i} className="flex items-center gap-6 px-6 font-default text-[18px] font-bold uppercase tracking-[0.15em] text-foreground/80">
              <span>{b}</span>
              <span style={{
            color: "var(--primary)"
          }}>·</span>
            </div>)}
        </div>
      </div>

      <style>{`
        @keyframes marquee {
          from { transform: translateX(0); }
          to { transform: translateX(-33.333%); }
        }
        @keyframes scrollLine {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(400%); }
        }
      `}</style>
    </section>;
}